import React, { useEffect, useCallback, useRef, useState } from 'react';
import ResizeDetector from 'element-resize-detector';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useIframeHeightOverride from 'hooks/use-iframe-height-override';

const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

/** Observes its own height and dispatches iframe resize overrides. */
export default function IframeHeightOverride(props) {
  const flags = useFlags();
  const useResizeObserver = flags['ecomm.resize-observer-override.rollout'] ?? false;

  const resizeDetectorRef = useRef();
  const elementRef = useRef();
  const [currentHeight, setCurrentHeight] = useState(0);

  useIframeHeightOverride(currentHeight);

  // Legacy: element-resize-detector (flag off)
  const handleResize = useCallback(
    (element) => {
      const newHeight = element.clientHeight;
      if (newHeight !== currentHeight) {
        setCurrentHeight(newHeight);
      }
    },
    [currentHeight]
  );

  useEffect(() => {
    if (useResizeObserver) return undefined;
    if (!isInIframe) return undefined;

    if (!resizeDetectorRef.current) {
      resizeDetectorRef.current = ResizeDetector({
        strategy: `scroll`,
      });
    }
    const erd = resizeDetectorRef.current;
    const el = elementRef.current;

    if (!el) return undefined;

    erd.listenTo(el, handleResize);
    handleResize(el);

    return () => erd.uninstall(el);
  }, [elementRef, useResizeObserver]); // eslint-disable-line react-hooks/exhaustive-deps

  // New: native ResizeObserver (flag on)
  useEffect(() => {
    if (!useResizeObserver) return undefined;
    if (!isInIframe) return undefined;

    const el = elementRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setCurrentHeight(entry.contentRect.height);
    });
    observer.observe(el);
    setCurrentHeight(el.clientHeight);

    return () => observer.disconnect();
  }, [useResizeObserver]);

  return <div ref={elementRef} {...props} />;
}
